import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import { getCurrentLevel, getCurrentTier, getXPProgress, tiers } from '../../constants/levels';
import { getLocalizedText } from '../../utils/localizationHelpers';
import { AVAILABLE_LANGUAGES, GAME_GENRES, PLATFORMS } from '../../constants/profile';
import InlineEditSection from '../shared/InlineEditSection';
import TierAnimalArt from './TierAnimalArt';
import useProfileEdit from '../../hooks/useProfileEdit';
import { useAuth } from '../../contexts/AuthContext';
import { PageShell } from '../shared';

export default function DashboardHome({
  userData = {}, stats = {}, activeQuest, availableQuests = [],
  pendingRequests = [], questHistory = [], onTabChange
}) {
  const { currentUser } = useAuth();
  const { questsCompleted = 0, questsAbandoned = 0, totalXP = 0 } = stats;
  const currentLevel = getCurrentLevel(totalXP);
  const currentTier = getCurrentTier(totalXP);
  const tierData = tiers[currentTier];
  const xpProgress = getXPProgress(totalXP);
  const completionRate = questsCompleted + questsAbandoned > 0
    ? Math.round((questsCompleted / (questsCompleted + questsAbandoned)) * 100) : 0;

  const username = userData.username || currentUser?.displayName || 'Streamer';
  const initials = username.charAt(0).toUpperCase();
  const isMaxLevel = currentLevel >= 50;

  const {
    editingSection, startEditing, cancelEditing, saveSection,
    editStreamingLanguages,
    editGenres, setEditGenres,
    editPlatforms, setEditPlatforms,
    editRegion, setEditRegion,
    editMatureContent, setEditMatureContent,
    editSocialLinks, setEditSocialLinks,
    handlePrimaryLanguageChange, handleSecondaryLanguageChange,
    removeSecondaryLanguage, getAvailableLanguages, getLanguageName,
    continentOptions, countriesByContinent, isCountriesLoaded, countriesLoadError,
    isLanguagesEmpty, isGenresEmpty,
    isPlatformsEmpty, isRegionEmpty,
    isSocialLinksValid, isSocialEmpty,
  } = useProfileEdit(userData);

  return (
    <PageShell className="dh">

      {/* ── Profile Bento Grid ── */}
      <div className="dh-bento">
        {/* Identity Card */}
        <div className="dh-bento-identity">
          <div className="dh-id-top">
            <div className="dh-id-avatar" style={{ '--tier-color': tierData.color }}>
              <div className="dh-id-avatar-ring">
                {userData.profileImageUrl ? (
                  <img src={userData.profileImageUrl} alt="" className="dh-id-avatar-img" />
                ) : (
                  <div className="dh-id-avatar-fallback">{initials}</div>
                )}
              </div>
              <span className="dh-id-level">{currentLevel}</span>
            </div>
            <div className="dh-id-info">
              <h1 className="dh-id-username">{username}</h1>
              <div className="dh-id-meta">
                <span className="dh-id-tier" style={{ color: tierData.color, borderColor: tierData.color + '30', background: tierData.color + '10' }}>
                  {tierData.name}
                </span>
                <div className="dh-social">
                  {editingSection !== 'social' && (
                    <>
                      {isSocialEmpty ? (
                        <button className="dh-social-add-btn" onClick={() => startEditing('social')}>
                          <FontAwesomeIcon icon={ICONS.PLUS} />
                          <span>Add social links</span>
                        </button>
                      ) : (
                        <>
                          {userData.socialLinks?.twitch && (
                            <a href={`https://twitch.tv/${userData.socialLinks.twitch}`}
                              target="_blank" rel="noopener noreferrer"
                              className="dh-social-link twitch" title={userData.socialLinks.twitch}>
                              <FontAwesomeIcon icon={ICONS.TWITCH} />
                            </a>
                          )}
                          {userData.socialLinks?.customLink?.link && (
                            <a href={userData.socialLinks.customLink.link}
                              target="_blank" rel="noopener noreferrer"
                              className="dh-social-link"
                              title={userData.socialLinks.customLink.name || 'Link'}>
                              <FontAwesomeIcon icon={ICONS.EXTERNAL_LINK} />
                            </a>
                          )}
                          <button
                            className="dh-social-edit-btn"
                            onClick={() => startEditing('social')}
                            title="Edit social links"
                          >
                            <FontAwesomeIcon icon={ICONS.EDIT} />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Social Edit Form */}
          {editingSection === 'social' && (
            <div className="dh-social-form" onClick={(e) => e.stopPropagation()}>
              <div className="dh-social-inputs">
                <div className="dh-social-input-group">
                  <FontAwesomeIcon icon={ICONS.TWITCH} className="dh-social-input-icon twitch" />
                  <input type="text" value={editSocialLinks.twitch}
                    onChange={(e) => setEditSocialLinks({ ...editSocialLinks, twitch: e.target.value })}
                    placeholder="Twitch username" className="dh-social-input" />
                </div>
                <div className="dh-social-input-group">
                  <FontAwesomeIcon icon={ICONS.LINK} className="dh-social-input-icon" />
                  <input type="text" value={editSocialLinks.customLink?.name || ''}
                    onChange={(e) => setEditSocialLinks({ ...editSocialLinks, customLink: { ...editSocialLinks.customLink, name: e.target.value } })}
                    placeholder="Link name" className="dh-social-input" />
                  <input type="text" value={editSocialLinks.customLink?.link || ''}
                    onChange={(e) => setEditSocialLinks({ ...editSocialLinks, customLink: { ...editSocialLinks.customLink, link: e.target.value } })}
                    placeholder="https://..." className="dh-social-input" />
                </div>
              </div>
              <div className="dh-social-actions">
                <button onClick={() => saveSection('social', { socialLinks: editSocialLinks })}
                  className="dh-social-save" disabled={!isSocialLinksValid()}>
                  <FontAwesomeIcon icon={ICONS.CHECK} /> Save
                </button>
                <button onClick={cancelEditing} className="dh-social-cancel">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {/* Profile Attributes */}
          <div className="dh-id-divider" />
          <div className="dh-id-attrs">
            <InlineEditSection
              icon={ICONS.GLOBE} title="Languages" compact
              isEmpty={isLanguagesEmpty}
              isEditing={editingSection === 'languages'}
              onStartEdit={() => startEditing('languages')}
              onSave={() => saveSection('languages', { streamingLanguages: editStreamingLanguages })}
              onCancel={cancelEditing}
              displayContent={
                isLanguagesEmpty ? <span className="no-preferences">Not set</span> : (() => {
                  const allLangs = [
                    ...(userData.streamingLanguages?.primary?.[0] ? [{ code: userData.streamingLanguages.primary[0], highlight: true }] : []),
                    ...(userData.streamingLanguages?.secondary || []).map(code => ({ code, highlight: false }))
                  ];
                  const visible = allLangs.slice(0, 2);
                  const overflow = allLangs.length - 2;
                  return (
                    <>
                      {visible.map(l => (
                        <span key={l.code} className={`profile-badge${l.highlight ? ' highlight' : ''}`}>{getLanguageName(l.code)}</span>
                      ))}
                      {overflow > 0 && <span className="profile-badge overflow">+{overflow}</span>}
                    </>
                  );
                })()
              }
              editContent={
                <div className="languages-edit-inline">
                  <select value={editStreamingLanguages.primary?.[0] || ''}
                    onChange={(e) => handlePrimaryLanguageChange(e.target.value)} className="language-dropdown-inline">
                    <option value="">Primary language</option>
                    {AVAILABLE_LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
                  </select>
                  {editStreamingLanguages.secondary?.length > 0 && (
                    <div className="selected-languages-inline">
                      {editStreamingLanguages.secondary.map(langCode => {
                        const lang = AVAILABLE_LANGUAGES.find(l => l.code === langCode);
                        return lang ? (
                          <div key={langCode} className="selected-language-item-inline">
                            <span>{lang.name}</span>
                            <button type="button" onClick={() => removeSecondaryLanguage(langCode)} className="remove-tag-btn-inline">
                              <FontAwesomeIcon icon={ICONS.TIMES} />
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  {editStreamingLanguages.secondary?.length < 5 && (
                    <select value="" onChange={(e) => handleSecondaryLanguageChange(e.target.value)} className="language-dropdown-inline">
                      <option value="">+ Secondary</option>
                      {getAvailableLanguages([...(editStreamingLanguages.primary || []), ...(editStreamingLanguages.secondary || [])])
                        .map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
                    </select>
                  )}
                </div>
              }
            />
            <InlineEditSection
              icon={ICONS.TAG} title="Genres" compact
              isEmpty={isGenresEmpty}
              isEditing={editingSection === 'genres'}
              onStartEdit={() => startEditing('genres')}
              onSave={() => saveSection('genres', { genres: editGenres })}
              onCancel={cancelEditing}
              displayContent={
                isGenresEmpty ? <span className="no-preferences">Not set</span> : (() => {
                  const all = (userData.genres || []).map(code => GAME_GENRES.find(x => x.code === code)).filter(Boolean);
                  const visible = all.slice(0, 2);
                  const overflow = all.length - 2;
                  return (
                    <>
                      {visible.map(g => <span key={g.code} className="profile-badge">{g.name}</span>)}
                      {overflow > 0 && <span className="profile-badge overflow">+{overflow}</span>}
                    </>
                  );
                })()
              }
              editContent={
                <div className="genres-edit-inline">
                  {editGenres.length > 0 && (
                    <div className="selected-tags-inline">
                      {editGenres.map(code => {
                        const opt = GAME_GENRES.find(g => g.code === code);
                        return opt ? (
                          <div key={code} className="tag-item-inline">
                            <span>{opt.name}</span>
                            <button type="button" className="remove-tag-btn-inline" onClick={() => setEditGenres(editGenres.filter(c => c !== code))}>
                              <FontAwesomeIcon icon={ICONS.TIMES} />
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  {editGenres.length < 3 && (
                    <select value="" onChange={(e) => {
                      const val = e.target.value;
                      if (val && !editGenres.includes(val) && editGenres.length < 3) setEditGenres([...editGenres, val]);
                    }} className="preference-dropdown-inline">
                      <option value="">+ Add genre (max 3)</option>
                      {GAME_GENRES.filter(g => !editGenres.includes(g.code)).map(g => <option key={g.code} value={g.code}>{g.name}</option>)}
                    </select>
                  )}
                </div>
              }
            />
            <InlineEditSection
              icon={ICONS.TASKS} title="Platforms" compact
              isEmpty={isPlatformsEmpty}
              isEditing={editingSection === 'platforms'}
              onStartEdit={() => startEditing('platforms')}
              onSave={() => saveSection('platforms', { platforms: editPlatforms })}
              onCancel={cancelEditing}
              displayContent={
                isPlatformsEmpty ? <span className="no-preferences">Not set</span> : (() => {
                  const all = (userData.platforms || []).map(code => PLATFORMS.find(x => x.code === code)).filter(Boolean);
                  const visible = all.slice(0, 2);
                  const overflow = all.length - 2;
                  return (
                    <>
                      {visible.map(p => <span key={p.code} className="profile-badge">{p.name}</span>)}
                      {overflow > 0 && <span className="profile-badge overflow">+{overflow}</span>}
                    </>
                  );
                })()
              }
              editContent={
                <div className="platforms-edit-inline">
                  {editPlatforms.length > 0 && (
                    <div className="selected-tags-inline">
                      {editPlatforms.map(code => {
                        const opt = PLATFORMS.find(p => p.code === code);
                        return opt ? (
                          <div key={code} className="tag-item-inline neutral">
                            <span>{opt.name}</span>
                            <button type="button" className="remove-tag-btn-inline" onClick={() => setEditPlatforms(editPlatforms.filter(c => c !== code))}>
                              <FontAwesomeIcon icon={ICONS.TIMES} />
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  <select value="" onChange={(e) => {
                    const val = e.target.value;
                    if (val && !editPlatforms.includes(val)) setEditPlatforms([...editPlatforms, val]);
                  }} className="preference-dropdown-inline">
                    <option value="">+ Add platform</option>
                    {PLATFORMS.filter(p => !editPlatforms.includes(p.code)).map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                  </select>
                </div>
              }
            />
            <InlineEditSection
              icon={ICONS.SHIELD} title="Content" compact
              isEmpty={false}
              isEditing={editingSection === 'content'}
              onStartEdit={() => startEditing('content')}
              onSave={() => saveSection('content', { matureContent: editMatureContent })}
              onCancel={cancelEditing}
              displayContent={
                <span className={`profile-badge ${userData.matureContent === 'mature-18' ? '' : 'highlight'}`}>
                  {userData.matureContent === 'mature-18' ? 'Mature 18+' : 'All Audiences'}
                </span>
              }
              editContent={
                <div className="content-edit-inline">
                  <select className="content-dropdown-inline" value={editMatureContent}
                    onChange={(e) => setEditMatureContent(e.target.value)}>
                    <option value="all-audiences">All Audiences</option>
                    <option value="mature-18">Mature 18+</option>
                  </select>
                </div>
              }
            />
            <InlineEditSection
              icon={ICONS.GLOBE} title="Region" compact
              isEmpty={isRegionEmpty}
              isEditing={editingSection === 'region'}
              onStartEdit={() => startEditing('region')}
              onSave={() => saveSection('region', { region: editRegion })}
              onCancel={cancelEditing}
              displayContent={
                isRegionEmpty ? <span className="no-preferences">Not set</span> : (
                  <>
                    <span className="profile-badge">{userData.region.continent}</span>
                    {userData.region.country && <span className="profile-badge">{userData.region.country}</span>}
                  </>
                )
              }
              editContent={
                <div className="region-edit-inline">
                  <select className="region-dropdown-inline" value={editRegion.continent || ''}
                    onChange={(e) => setEditRegion({ continent: e.target.value, country: '' })}>
                    <option value="">{countriesLoadError ? 'Failed' : (isCountriesLoaded ? 'Continent' : 'Loading...')}</option>
                    {isCountriesLoaded && continentOptions.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                  <select className="region-dropdown-inline" value={editRegion.country || ''}
                    onChange={(e) => setEditRegion({ ...editRegion, country: e.target.value })}
                    disabled={!editRegion.continent || !isCountriesLoaded}>
                    <option value="">Country</option>
                    {(countriesByContinent[editRegion.continent] || []).map(ct => <option key={ct.code} value={ct.code}>{ct.name}</option>)}
                  </select>
                </div>
              }
            />
          </div>
        </div>

        {/* XP & Progress Card */}
        <div className="dh-bento-xp" style={{ '--tier-color': tierData.color }}>
          <TierAnimalArt tier={currentTier} />
          <div className="dh-xp-header">
            <span className="dh-xp-total">{totalXP.toLocaleString()}</span>
            <span className="dh-xp-unit">XP</span>
          </div>
          <div className="dh-xp-track">
            <div className="dh-xp-bar-bg">
              <div
                className="dh-xp-bar-fill"
                style={{
                  width: `${isMaxLevel ? 100 : xpProgress.percentage}%`,
                }}
              />
            </div>
            <div className="dh-xp-labels">
              {!isMaxLevel ? (
                <span className="dh-xp-next">{xpProgress.current} / {xpProgress.needed} to Lv. {currentLevel + 1}</span>
              ) : (
                <span className="dh-xp-next">MAX LEVEL</span>
              )}
            </div>
          </div>
          <div className="dh-xp-stats">
            <div className="dh-stat-cell">
              <span className="dh-stat-num">{questsCompleted}</span>
              <span className="dh-stat-label">Completed</span>
            </div>
            <div className="dh-stat-cell">
              <span className="dh-stat-num">{completionRate}%</span>
              <span className="dh-stat-label">Success</span>
            </div>
            <div className="dh-stat-cell">
              <span className="dh-stat-num">{availableQuests.length}</span>
              <span className="dh-stat-label">Available</span>
            </div>
          </div>
        </div>

      </div>

      {/* ── Active Quest — Immersive Hero ── */}
      <div className="dh-section">
        <div className="dh-section-label">
          <span className="dh-section-dot" />
          ACTIVE QUEST
        </div>

        {activeQuest ? (
          <div className="dh-mission-hero" onClick={() => onTabChange('current')}>
            {activeQuest.icon && (
              <div className="dh-mission-hero-bg" style={{ backgroundImage: `url(${activeQuest.icon})` }} />
            )}
            <div className="dh-mission-hero-gradient" />
            <div className="dh-mission-hero-content">
              <div className="dh-mission-hero-info">
                {activeQuest.icon && (
                  <img src={activeQuest.icon} alt="" className="dh-mission-hero-icon" />
                )}
                <div className="dh-mission-hero-text">
                  <span className="dh-mission-hero-title">{getLocalizedText(activeQuest.title)}</span>
                  <div className="dh-mission-hero-meta">
                    {activeQuest.difficulty && (
                      <span className={`dh-pill dh-pill-diff ${activeQuest.difficulty}`}>{activeQuest.difficulty}</span>
                    )}
                    {activeQuest.value && (
                      <span className="dh-pill dh-pill-xp">
                        <FontAwesomeIcon icon={ICONS.COINS} /> {activeQuest.value} XP
                      </span>
                    )}
                    {activeQuest.sideQuests?.length > 0 && (
                      <span className="dh-pill">{activeQuest.sideQuests.length} Side Quests</span>
                    )}
                  </div>
                </div>
              </div>
              {activeQuest.sideQuests?.length > 0 && (
                <div className="dh-hero-sidequests">
                  <span className="dh-hero-sq-label">Side Quests</span>
                  {activeQuest.sideQuests.slice(0, 4).map((sq, i) => (
                    <div key={i} className="dh-hero-sq-item">
                      <span className={`dh-hero-sq-dot ${sq.difficulty || ''}`} />
                      <span className="dh-hero-sq-title">{getLocalizedText(sq.title)}</span>
                    </div>
                  ))}
                  {activeQuest.sideQuests.length > 4 && (
                    <span className="dh-hero-sq-more">+{activeQuest.sideQuests.length - 4} more</span>
                  )}
                </div>
              )}
              <div className="dh-hero-chevron">
                <FontAwesomeIcon icon={ICONS.CHEVRON_RIGHT} />
              </div>
            </div>
          </div>
        ) : (
          <div className="dh-mission-card dh-mission-empty">
            <div className="dh-mission-accent empty" />
            <div className="dh-mission-body">
              <div className="dh-mission-top">
                <div className="dh-mission-info">
                  <div className="dh-empty-icon">
                    <FontAwesomeIcon icon={ICONS.SCROLL} />
                  </div>
                  <div className="dh-mission-text">
                    <span className="dh-mission-title faded">No active quest</span>
                    <span className="dh-mission-sub">Browse available quests to start your next quest</span>
                  </div>
                </div>
                <button className="dh-cta" onClick={() => onTabChange('available')}>
                  Browse Quests
                  <FontAwesomeIcon icon={ICONS.CHEVRON_RIGHT} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Available Quests — Full Width Grid ── */}
      {availableQuests.length > 0 && (
        <div className="dh-section">
          <div className="dh-section-label">
            <span className="dh-section-dot" />
            AVAILABLE QUESTS
            <span className="dh-section-count">{availableQuests.length}</span>
          </div>

          <div className="dh-quest-grid">
            {availableQuests.slice(0, 4).map(quest => (
              <div key={quest.id} className="dh-quest-tile" onClick={() => onTabChange('available')}>
                {quest.icon && (
                  <div className="dh-tile-bg" style={{ backgroundImage: `url(${quest.icon})` }} />
                )}
                <div className="dh-tile-gradient" />
                <div className="dh-tile-content">
                  <div className="dh-tile-top">
                    <span className="dh-tile-xp">
                      <FontAwesomeIcon icon={ICONS.COINS} />
                      {quest.value} XP
                    </span>
                  </div>
                  <div className="dh-tile-bottom">
                    <span className="dh-tile-title">{getLocalizedText(quest.title)}</span>
                    <div className="dh-tile-meta">
                      {quest.difficulty && (
                        <span className={`dh-pill-sm ${quest.difficulty}`}>{quest.difficulty}</span>
                      )}
                      {quest.sideQuests?.length > 0 && (
                        <span className="dh-pill-sm">{quest.sideQuests.length} Side Quests</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button className="dh-view-all" onClick={() => onTabChange('available')}>
            View All Quests
            <FontAwesomeIcon icon={ICONS.CHEVRON_RIGHT} />
          </button>
        </div>
      )}

      {/* ── Pending Approvals ── */}
      {pendingRequests.length > 0 && (
        <div className="dh-pending-strip">
          <div className="dh-pending-accent" />
          <FontAwesomeIcon icon={ICONS.CLOCK} className="dh-pending-icon" />
          <div className="dh-pending-info">
            <span className="dh-pending-text">
              {pendingRequests.length} quest request{pendingRequests.length > 1 ? 's' : ''} pending admin review
            </span>
          </div>
          <button className="dh-view-link" onClick={() => onTabChange('questlog')}>
            View <FontAwesomeIcon icon={ICONS.CHEVRON_RIGHT} />
          </button>
        </div>
      )}
    </PageShell>
  );
}
